import { useMemo, useState } from 'react';
import { Alert, Pressable, Text, View } from 'react-native';
import { setDoc, useDoc } from '../store/store';
import { concluirPend, copiarParaHoje, desfazerDispensa, dispensarPend, moverParaHoje, pendencias, type Pendencia } from '../store/planner';
import { ActionSheet, type Action } from './ui';
import { C, useCores } from './theme';
import { addDays, horaTela, labelDia, today } from '../lib/date';
import { t } from '../lib/i18n';

const COR = '#F59E0B';

/** No Planner de hoje: o que ficou sem concluir nos dias anteriores, para trazer para hoje ou dar baixa. */
export function Pendentes() {
  const tasks = useDoc('planner').tasks;
  const aj = useDoc('ajustes');
  const { accent } = useCores();
  const hoje = today();
  const lista = useMemo(() => pendencias(tasks, hoje, aj.pendDispensadas), [tasks, hoje, aj.pendDispensadas]);
  const [aberto, setAberto] = useState(false);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const desfazer = aj.pendDesfazer?.dia === hoje ? aj.pendDesfazer.chaves : [];
  const recolhido = aj.pendRecolhido === hoje;
  const recolher = (v: boolean) => setDoc('ajustes', d => ({ ...d, pendRecolhido: v ? hoje : undefined }));
  const linhaDesfazer = desfazer.length > 0 && (
    <Pressable onPress={() => desfazerDispensa()} hitSlop={6} style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('{n} dispensada(s) hoje', { n: desfazer.length })} · <Text style={{ color: COR, fontWeight: '800' }}>↩︎ {t('Desfazer')}</Text></Text>
    </Pressable>
  );
  if (!lista.length) return desfazer.length ? <View style={{ paddingHorizontal: 4 }}>{linhaDesfazer}</View> : null;
  const deOntem = lista.every(p => p.data === addDays(hoje, -1));
  const mostrar = aberto ? lista : lista.slice(0, 3);

  const opcoes = (p: Pendencia) => setMenu({ t: `${p.t.emoji} ${p.t.title}`, a: [
    { label: '📋 ' + t('Copiar para hoje'), onPress: () => copiarParaHoje(p) },
    { label: '➡️ ' + t('Mover para hoje'), onPress: () => moverParaHoje(p) },
    { label: '✅ ' + t('Marcar como concluída ({dia})', { dia: labelDia(p.data).toLowerCase() }), onPress: () => concluirPend(p) },
    { label: t('Dispensar o aviso'), onPress: () => dispensarPend([p], true) },
  ] });
  const todas = () => Alert.alert(t('Trazer tudo para hoje?'), t('{n} tarefa(s) pendente(s) vão para a lista de hoje.', { n: lista.length }), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Copiar'), onPress: () => lista.forEach(p => copiarParaHoje(p)) },
    { text: t('Mover'), onPress: () => lista.forEach(p => moverParaHoje(p)) },
  ]);

  return (
    <View style={{ backgroundColor: 'rgba(245,158,11,0.10)', borderRadius: 16, padding: 12, gap: 8, borderWidth: 1, borderColor: 'rgba(245,158,11,0.45)' }}>
      <Pressable onPress={() => (recolhido ? recolher(false) : setAberto(x => !x))} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Pressable onPress={() => recolher(!recolhido)} hitSlop={10} accessibilityLabel={recolhido ? t('Abrir') : t('Recolher')}>
          <Text style={{ color: C.sub, fontSize: 16 }}>{recolhido ? '▸' : '▾'}</Text>
        </Pressable>
        <Text style={{ fontSize: 18 }}>⏮️</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>
            {deOntem ? t('{n} tarefa(s) de ontem sem concluir', { n: lista.length }) : t('{n} tarefa(s) dos últimos dias sem concluir', { n: lista.length })}
          </Text>
          {!recolhido && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Toque numa tarefa para copiar ou mover para hoje.')}</Text>}
        </View>
        {!recolhido && lista.length > 3 && <Text style={{ color: accent, fontWeight: '700', fontSize: 12 }}>{aberto ? t('Mostrar menos') : t('Ver todos')}</Text>}
      </Pressable>
      {!recolhido && mostrar.map(p => (
        <Pressable key={`${p.t.id}@${p.data}`} onPress={() => opcoes(p)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 12, padding: 10 }}>
          <Text style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{p.t.emoji}</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={1}>{p.t.title}</Text>
            <Text style={{ color: C.sub, fontSize: 11 }}>{labelDia(p.data)}{p.t.time ? ` · ${horaTela(p.t.time)}` : ''}</Text>
          </View>
          <Pressable onPress={() => copiarParaHoje(p)} hitSlop={8} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10, backgroundColor: COR }}>
            <Text style={{ color: '#111', fontWeight: '800', fontSize: 12 }}>{t('Para hoje')}</Text>
          </Pressable>
          <Pressable onPress={() => dispensarPend([p], true)} hitSlop={8} accessibilityLabel={t('Dispensar')}>
            <Text style={{ color: C.sub, fontSize: 16 }}>✕</Text>
          </Pressable>
        </Pressable>
      ))}
      {!recolhido && linhaDesfazer}
      {!recolhido && <View style={{ flexDirection: 'row', gap: 14, justifyContent: 'flex-end' }}>
        <Pressable onPress={() => dispensarPend(lista, true)} hitSlop={8}><Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>{t('Dispensar todas')}</Text></Pressable>
        {lista.length > 1 && <Pressable onPress={todas} hitSlop={8}><Text style={{ color: COR, fontSize: 12, fontWeight: '800' }}>{t('Trazer todas para hoje')}</Text></Pressable>}
      </View>}
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
    </View>
  );
}
